import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, CalendarClock, Loader2, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useFormatters } from '@/lib/hooks/useFormatters'
import { apiErrorMessage } from '@/lib/checkout/outcome'
import { authFailureText } from '@/lib/authFailure'
import type { ErasureBlock } from '@/lib/erasure'
import { sdk } from '@/lib/sdk'

interface ErasureLoginNoticeProps {
  block: ErasureBlock
  email: string
  password: string
  onBack: () => void
  /** The erasure was given up: the dialog signs in with the same e-mail and password. */
  onCanceled: () => Promise<void>
}

/**
 * F-199: the login answered 403 `ERASURE_SCHEDULED` (no paid access, so no
 * session until the date). Says when, and offers to give up with the
 * password just typed (`POST /api/privacy/erasure/cancel`, with the login's
 * ALTCHA), then signs in. A team erasure has no way back: only the text.
 */
export const ErasureLoginNotice = ({ block, email, password, onBack, onCanceled }: ErasureLoginNoticeProps) => {
  const { t } = useTranslation(['privacy', 'auth'])
  const { formatLongDate } = useFormatters()
  const [sending, setSending] = React.useState(false)
  const [heavy, setHeavy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const giveUp = async () => {
    setSending(true)
    setHeavy(false)
    setError(null)
    try {
      await sdk.auth.cancelErasureWithLogin(email, password, (level) => setHeavy(level > 1))
      await onCanceled()
    } catch (err) {
      setError(authFailureText(err, t) ?? apiErrorMessage(err).message ?? (err as Error)?.message ?? t('erasure.error'))
    } finally {
      setSending(false)
      setHeavy(false)
    }
  }

  const date = block.erasureScheduledAt ? formatLongDate(block.erasureScheduledAt) : ''
  return (
    <div className="space-y-5" data-testid="erasure-login-notice">
      <CalendarClock className="h-10 w-10 text-warning-text" />
      <div>
        <h2 className="text-2xl font-semibold text-foreground">{t('erasure.login.title')}</h2>
        <p className="mt-2 text-base text-foreground-secondary">
          {block.cancelable ? t('erasure.login.body', { date }) : t('erasure.login.bodyTeam')}
        </p>
        {block.cancelable && <p className="mt-2 text-sm text-muted-foreground">{t('erasure.login.hint')}</p>}
      </div>
      {error && <p role="alert" className="rounded-xl bg-danger-surface p-3 text-sm text-danger-text">{error}</p>}
      <div className="flex flex-col gap-2">
        {block.cancelable && (
          <Button type="button" variant="secondary" className="h-12 w-full gap-2 rounded-xl text-base" onClick={() => void giveUp()} disabled={sending} data-testid="erasure-login-give-up">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
            {sending ? (heavy ? t('auth:guard.verifying') : t('erasure.pending.givingUp')) : t('erasure.login.giveUp')}
          </Button>
        )}
        <Button type="button" variant="ghost" className="w-full gap-2" onClick={onBack} disabled={sending}>
          <ArrowLeft className="h-4 w-4" />{t('erasure.login.back')}
        </Button>
      </div>
    </div>
  )
}
